import { DETAIL_ROUTE_SHAPES, detailRoutePath as sharedDetailRoutePath, detailRouteKeys, type DetailRouteKey } from "@household/shared/detail-routes";

import type { Tab } from "../features/shell/navigation.js";
import type { EntityReferenceId } from "./types.js";

/*
 * The query-parameter detail routes (#533).
 *
 * Some detail views have a path of their own — `/inventory/assets/{id}`,
 * `/recipes/{id}` — and `routing.ts` models those, because the shell has to
 * decide which tab a cold URL opens before any feature is mounted. The rest
 * live on their tab's own path with the object id in the query string, and
 * those were the ones that went wrong: three places decided what such a URL
 * looks like and none of them was in charge.
 *
 *   1. `routing.ts` knew only the path-segment ids, so a query-borne id was
 *      invisible to the shell.
 *   2. Each feature hand-rolled `new URLSearchParams(window.location.search)`,
 *      at three different levels of completeness — shopping and projects both
 *      read AND wrote, chores only ever read.
 *   3. `entityRoutes.ts` built `/shopping?listId=…` out of string literals for
 *      audit and search deep links, so it was the one place that published the
 *      shapes without being the place that implemented them.
 *
 *   4. And one the issue body never named, on the far side of the process
 *      boundary: `apps/api/src/services/semanticSearch.ts` composes the
 *      PERSISTED `routePath` of every search document, and `agentReadTools.ts`
 *      the `routePath` of every assistant fact. A parameter name that lives
 *      only in the web bundle is a parameter name the server has to guess, and
 *      it guessed the bare tab.
 *
 * The table is now the authority, and since the fourth source is a different
 * process the table itself lives in `@household/shared/detail-routes`. This
 * module is the WEB view of it: the shared half is the path and the parameter
 * name, and the `Tab` added here is the part only the web shell has a use for.
 * `entityRoutes.ts` builds its paths from it, each feature's routing module
 * reads and writes through it, and `app/routing.ts` canonicalises through it,
 * so the parameter name for an object exists exactly once in the repository.
 */

export type { DetailRouteKey };

export type DetailRouteDefinition = {
  /** The tab the detail view lives on, and the path its URL is built from. */
  tab: Tab;
  path: string;
  /** The query parameter that names the open object. */
  param: string;
};

/**
 * The shared shapes with each route's tab attached. Spelling the keys out (as
 * opposed to mapping over `detailRouteKeys`) is what makes a route added to the
 * shared table a COMPILE error here rather than a route with no tab: the shell
 * cannot canonicalise a detail id whose tab it does not know.
 */
export const DETAIL_ROUTES: Record<DetailRouteKey, DetailRouteDefinition> = {
  shoppingList: { tab: "shopping", ...DETAIL_ROUTE_SHAPES.shoppingList },
  shoppingTrip: { tab: "shopping", ...DETAIL_ROUTE_SHAPES.shoppingTrip },
  chore: { tab: "chores", ...DETAIL_ROUTE_SHAPES.chore },
  project: { tab: "projects", ...DETAIL_ROUTE_SHAPES.project }
};

/** The detail routes that live on `tab`, in table order. A tab may own more
 * than one — shopping carries both `listId` and `tripId`. */
export function detailRouteKeysForTab(tab: Tab): DetailRouteKey[] {
  return detailRouteKeys.filter((key) => DETAIL_ROUTES[key].tab === tab);
}

/**
 * The canonical path that opens `id`. This is what a deep link carries and what
 * the view writes when the reader opens the object, so the two cannot drift.
 * Without an id it is the index — the honest landing for a reference that names
 * no particular object.
 */
export function detailRoutePath(key: DetailRouteKey, id: EntityReferenceId | null | undefined): string {
  return sharedDetailRoutePath(key, id ?? null);
}

/** The id the current URL names for this route, or null when it names none. */
export function detailRouteIdFromUrl(key: DetailRouteKey): string | null {
  return new URLSearchParams(window.location.search).get(DETAIL_ROUTES[key].param);
}

/**
 * Every detail id `search` names for the routes that live on `tab`.
 *
 * This is what puts a query-borne id on an `AppRoute`, so `canonicalPathForRoute`
 * can rebuild a URL that opens the same object. It is keyed by tab because that
 * is the only thing the shell knows before a feature mounts, and because a
 * parameter is only meaningful on the page that reads it: a stray `?listId=` on
 * `/chores` names nothing and must not survive canonicalisation.
 */
export function detailIdsFromUrl(tab: Tab, search: string = window.location.search): Partial<Record<DetailRouteKey, string>> {
  const params = new URLSearchParams(search);
  const ids: Partial<Record<DetailRouteKey, string>> = {};
  for (const key of detailRouteKeysForTab(tab)) {
    const value = params.get(DETAIL_ROUTES[key].param);
    if (value) ids[key] = value;
  }
  return ids;
}

/**
 * The one writer for a detail view's query string, generalized from the version
 * `features/projects/routing.ts` grew first.
 *
 * A key set to null (or an empty string) is removed, a key absent from `update`
 * is left alone, and values are encoded by URLSearchParams. The hash is carried
 * across untouched — the projects workspace keeps its open document there. No
 * history entry is written when nothing would change, so a corrective call
 * cannot pile up dead Back steps.
 *
 * `replace` is the difference between navigation and correction: opening an
 * object because the reader asked pushes, while auto-selecting one or clearing
 * an id that turned out to be dead replaces, so Back never returns to a URL
 * that resolves the same way it just did.
 */
export function updateDetailQuery(
  update: Partial<Record<string, string | null>>,
  replace = false,
  path: string = window.location.pathname
): void {
  const params = new URLSearchParams(window.location.search);
  for (const [key, value] of Object.entries(update)) {
    if (value) params.set(key, value);
    else params.delete(key);
  }
  const query = params.toString();
  const nextPath = `${path}${query ? `?${query}` : ""}${window.location.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current === nextPath) return;
  window.history[replace ? "replaceState" : "pushState"](null, "", nextPath);
}
